/**
 * Pure ISO-date arithmetic. No Date objects at all — the engine never reads
 * the clock (§8.2), and calendar maths on y/m/d integers has no timezone to
 * get wrong. All inputs and outputs are "YYYY-MM-DD".
 */
import type { ISODate } from "./types.js";

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidISODate(value: string): value is ISODate {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) return false;
  return day >= 1 && day <= daysInMonth(year, month);
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const length = lengths[month - 1];
  if (length === undefined) throw new Error(`Invalid month: ${month}`);
  return length;
}

function parts(date: ISODate): { year: number; month: number; day: number } {
  const m = ISO_DATE.exec(date);
  if (!m) throw new Error(`Not an ISO date: ${date}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, "0");
}

/** Negative when a < b, zero when equal, positive when a > b. Lexicographic works for ISO dates, but be explicit. */
export function compareISODate(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Add calendar months, clamping the day to the target month's length —
 * "2026-03-31" + 6 months = "2026-09-30". Matches how a period "held since"
 * a date reaches its anniversary.
 */
export function addMonthsISO(date: ISODate, months: number): ISODate {
  const { year, month, day } = parts(date);
  const zeroBased = month - 1 + months;
  const targetYear = year + Math.floor(zeroBased / 12);
  const targetMonth = ((zeroBased % 12) + 12) % 12 + 1;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth));
  return `${pad(targetYear, 4)}-${pad(targetMonth, 2)}-${pad(targetDay, 2)}`;
}

export function maxISODate(dates: readonly ISODate[]): ISODate {
  if (dates.length === 0) throw new Error("maxISODate of empty list");
  return dates.reduce((a, b) => (compareISODate(a, b) >= 0 ? a : b));
}
